<template>
  <div class="announcement-content" ref="container" @scroll="onScroll">
    <div class="header-actions">
      <div class="search-container">
        <input type="text" placeholder="Search announcements by id or title" v-model="searchQuery" class="search-input"
          @keyup.enter="onSearch" />
      </div>

      <div class="filter-group">
        <button v-for="option in visibleScopeOptions" :key="option.value" @click="setScope(option.value)"
          :class="['scope-filter-btn', { active: scopeFilter === option.value }]">
          {{ option.label }}
        </button>

        <date-range-picker ref="dateRangePicker" :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
          :showWeekNumbers="true" :showDropdowns="true" :autoApply="true" v-model="dateRangeFilter" class="date-input"
          control-container-class="date-control" opens="left" :ranges="dateRangePresets">
          <template v-slot:input="picker">
            <i class="fal fa-calendar-alt"></i>
            <span class="text-sm" v-if="picker.startDate || picker.endDate">
              {{ formatShortDate(picker.startDate) }} - {{ formatShortDate(picker.endDate) }}
            </span>
            <span class="text-sm" v-else>Select Date Range</span>
            <i @click.stop="resetDateRange" class="fa fa-times ms-auto" v-if="picker.startDate || picker.endDate"></i>
          </template>
        </date-range-picker>

        <button class="filter-btn" @click="onSearch">
          Search
          <i class="fas fa-search"></i>
        </button>

        <button id="ann-sort-btn" class="filter-btn" @click="toggleSort">
          <i :class="['fas', sortOrder === 'recent' ? 'fa-sort-amount-up' : 'fa-sort-amount-down']"></i>
        </button>
        <b-tooltip target="ann-sort-btn" placement="top">
          {{ sortOrder === 'recent' ? 'Newest First' : 'Oldest First' }}
        </b-tooltip>

        <button class="create-btn" @click="openCreateModal">
          <img src="@/assets/images/add-border-icon.svg" class="btn-icon" alt="Add" />
          CREATE
        </button>
      </div>
    </div>

    <div class="table-container">
      <SkeletonTableLoader v-if="loading && !loadingMore" :rowCount="7" :columnCount="6" />

      <div v-else-if="!loading && !announcements.length" class="no-data-container">
        <svg class="no-data-icon" viewBox="0 0 512 512" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="M480 32c0-12.9-7.8-24.6-19.8-29.6s-25.7-2.2-34.9 6.9L381.7 53c-48 48-113.1 75-181 75H56c-13.3 0-24 10.7-24 24v8H24C10.7 160 0 170.7 0 184v80c0 13.3 10.7 24 24 24h8v8c0 13.3 10.7 24 24 24h36.8c-2.5 9.9-3.8 20.3-3.8 31.1 0 25.3 7.5 48.8 20.3 68.5 6.9 10.6 18.8 16.4 31.4 16.4h25.5c25.6 0 42.2-26.8 31-49.8-5.8-12-9.2-25.5-9.2-39.8 0-7.7 1-15.2 2.8-22.3h21.7c67.9 0 133 27 181 75l43.6 43.6c9.2 9.2 22.9 11.9 34.9 6.9S480 492.9 480 480V340.8c18.6-8.8 32-32.5 32-60.8s-13.4-52-32-60.8V32zM448 162.5V397.5c-1.3-1.2-2.6-2.4-3.9-3.6-50.5-50.5-118-80.6-189.1-84.4V250.4c71.1-3.7 138.6-33.9 189.1-84.4 1.3-1.2 2.6-2.4 3.9-3.6z" />
        </svg>
        <p class="no-data-title">{{ hasActiveFilters ? 'No matching announcements' : 'No announcements yet' }}</p>
        <p class="no-data-subtext">{{ hasActiveFilters ? 'Try adjusting your search or filters.' : 'Create your first announcement to get started.' }}</p>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Type</th>
              <th>Reference ID</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="announcement in announcements" :key="announcement.id">
              <td>{{ announcement.id }}</td>
              <td>{{ announcement.title }}</td>
              <td>
                <span :class="['scope-badge', equalsIgnoreCase(announcement.entityType, AnnouncementScope.ProductVariant) ? 'productvariant' : 'global']">
                  {{ scopeLabel(announcement) }}
                </span>
              </td>
              <td>{{ announcement.entityID || '—' }}</td>
              <td>{{ formatDateTime(announcement.date) }}</td>
              <td>
                <div class="actions-dropdown">
                  <button class="actions-btn" @click="toggleActionMenu(announcement.id)">
                    ACTIONS
                    <i class="fas fa-chevron-down" />
                  </button>
                  <div v-if="activeActionMenu === announcement.id" class="custom-dropdown-menu">
                    <button class="action-option" @click="editAnnouncement(announcement)">
                      <i class="fas fa-edit" />
                      Edit
                    </button>
                    <button class="action-option text-red-500" @click="deleteSelectedAnnouncement(announcement)">
                      <i class="fas fa-trash" />
                      Delete
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="loadingMore" class="loading-more-indicator">
          <div class="spinner"></div>
          <span>Loading more announcements...</span>
        </div>

        <div v-if="!hasMore && announcements.length > 0 && !loadingMore" class="all-fetched-indicator">
          <i class="fas fa-check-circle tick-icon"></i>
          <span>All announcements have been loaded.</span>
        </div>
      </div>
    </div>

    <ManageAnnouncement :visible="showModal" :isEditMode="isEditing" :announcementData="selectedAnnouncementData"
      @updateModalState="toggleManageModal"
      @editComplete="() => { isEditing = false; selectedAnnouncementData = null; }" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import ManageAnnouncement from '../../components/Admin/Announcement/ManageAnnouncement.vue';
import SkeletonTableLoader from '@/components/Shared/SkeletonTableLoader.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import { formatToDDMMMYY_HHmm, toShortDateString } from '../../helpers/FormatHelpers';
import { AnnouncementScope } from '../../constants/announcement';
import { UserType } from '../../constants/user';

export default {
  name: 'AnnouncementManagement',
  components: { ManageAnnouncement, SkeletonTableLoader, DateRangePicker },
  data() {
    return {
      AnnouncementScope,
      searchQuery: '',
      scopeFilter: 'all',
      scopeOptions: [
        { label: 'All', value: 'all' },
        { label: 'Global', value: 'global' },
        { label: 'Course', value: 'course' },
      ],
      dateRangeFilter: { startDate: null, endDate: null },
      sortOrder: 'recent',
      announcements: [],
      nextPageToken: null,
      pageSize: 10,
      loading: false,
      loadingMore: false,
      showModal: false,
      activeActionMenu: null,
      isEditing: false,
      selectedAnnouncementData: null,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    isTrainer() {
      return this.equalsIgnoreCase(this.user?.type || this.user?.role, UserType.TRAINER);
    },
    visibleScopeOptions() {
      // A trainer only ever has course announcements (list is already creator-scoped, no global),
      // so All/Global/Course filtering is meaningless — hide the scope buttons entirely.
      return this.isTrainer ? [] : this.scopeOptions;
    },
    hasMore() {
      return !!this.nextPageToken;
    },
    hasActiveFilters() {
      return !!(this.searchQuery || this.scopeFilter !== 'all' || this.dateRangeFilter.startDate || this.dateRangeFilter.endDate);
    },
    entityTypeForScope() {
      // Global is the null case (EntityType IS NULL) — filtered via the onlyGlobal flag, not an entityType value.
      if (this.scopeFilter === 'course') return AnnouncementScope.ProductVariant;
      return null;
    },
    inclusiveStartDate() {
      const start = this.dateRangeFilter?.startDate;
      return start
        ? new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0).toISOString()
        : null;
    },
    inclusiveEndDate() {
      const end = this.dateRangeFilter?.endDate;
      return end
        ? new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).toISOString()
        : null;
    },
    dateRangePresets() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return {
        Today: [today, today],
        Yesterday: [yesterday, yesterday],
        'This month': [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)],
        'Last month': [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)],
        'Last 3 months': [new Date(today.getFullYear(), today.getMonth() - 2, 1), today],
        'This year': [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)],
      };
    },
  },
  methods: {
    ...mapActions('announcement', ['fetchAnnouncements', 'deleteAnnouncement']),

    equalsIgnoreCase(a, b) {
      if (a == null || b == null) return a === b;
      return String(a).toLowerCase() === String(b).toLowerCase();
    },

    filterParams(extra = {}) {
      return {
        entityType: this.entityTypeForScope,
        onlyGlobal: this.scopeFilter === 'global',
        searchQuery: this.searchQuery || null,
        startDate: this.inclusiveStartDate,
        endDate: this.inclusiveEndDate,
        isNewestFirst: this.sortOrder === 'recent',
        pageSize: this.pageSize,
        ...extra,
      };
    },

    async reloadAnnouncements() {
      if (this.loading) return;
      this.loading = true;
      this.activeActionMenu = null;
      try {
        const result = await this.fetchAnnouncements(this.filterParams());
        this.announcements = result.payload || [];
        this.nextPageToken = result.nextPageToken || null;
        this.resetScroll();
      } catch (error) {
        this.notifyError('Unable to load announcements.');
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.loadingMore = true;
      try {
        const result = await this.fetchAnnouncements(this.filterParams({ nextPageToken: this.nextPageToken }));
        this.announcements = this.announcements.concat(result.payload || []);
        this.nextPageToken = result.nextPageToken || null;
      } catch (error) {
        this.notifyError('Failed to load more announcements.');
      } finally {
        this.loadingMore = false;
      }
    },
    async onScroll() {
      const container = this.$refs.container;
      if (!container || this.loading || this.loadingMore || !this.nextPageToken) return;
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
        await this.loadMore();
      }
    },
    resetScroll() {
      this.$nextTick(() => {
        if (this.$refs.container) this.$refs.container.scrollTop = 0;
      });
    },

    onSearch() {
      this.reloadAnnouncements();
    },
    setScope(scope) {
      this.scopeFilter = scope;
      this.reloadAnnouncements();
    },
    toggleSort() {
      this.sortOrder = this.sortOrder === 'recent' ? 'oldest' : 'recent';
      this.reloadAnnouncements();
    },
    resetDateRange() {
      this.dateRangeFilter = { startDate: null, endDate: null };
      this.reloadAnnouncements();
    },

    openCreateModal() {
      this.isEditing = false;
      this.selectedAnnouncementData = null;
      this.showModal = true;
    },
    toggleManageModal(state) {
      this.showModal = state !== undefined ? state : !this.showModal;
      if (!this.showModal) {
        this.selectedAnnouncementData = null;
        this.isEditing = false;
        this.reloadAnnouncements();
      }
    },
    toggleActionMenu(id) {
      this.activeActionMenu = this.activeActionMenu === id ? null : id;
    },
    editAnnouncement(announcement) {
      this.selectedAnnouncementData = announcement;
      this.isEditing = true;
      this.showModal = true;
      this.activeActionMenu = null;
    },
    async deleteSelectedAnnouncement(announcement) {
      if (!announcement?.id) return;
      this.activeActionMenu = null;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete the announcement "${announcement.title}"?`,
        { title: 'Confirm Deletion', okTitle: 'Yes, Delete', cancelTitle: 'Cancel', centered: true, size: 'md', okVariant: 'danger' }
      );
      if (!confirmed) return;

      try {
        await this.deleteAnnouncement(announcement.id);
        this.announcements = this.announcements.filter(item => item.id !== announcement.id);
        this.notifySuccess('Announcement deleted successfully.');
      } catch (error) {
        this.notifyError('Failed to delete announcement. Please try again.');
      }
    },

    scopeLabel(announcement) {
      const isCourseAnnouncement = this.equalsIgnoreCase(announcement.entityType, AnnouncementScope.ProductVariant);
      return isCourseAnnouncement ? 'Course' : 'Global';
    },
    formatDateTime(date) {
      if (!date) return 'N/A';
      return formatToDDMMMYY_HHmm(date);
    },
    formatShortDate(date) {
      return date ? toShortDateString(date) : '';
    },
    notifyError(message) {
      this.$toasted.error(message, { icon: 'exclamation-triangle', theme: 'outline', position: 'bottom-center', singleton: true, duration: 3000 });
    },
    notifySuccess(message) {
      this.$toasted.success(message, { icon: 'check-circle', theme: 'outline', position: 'bottom-center', singleton: true, duration: 3000 });
    },
  },
  mounted() {
    this.reloadAnnouncements();
  },
};
</script>

<style scoped>
.announcement-content {
  height: calc(100svh - var(--header-height));
  padding: 1.5rem;
  overflow: auto;
  background-color: #f5f5f5;
}

.header-actions {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-container {
  flex: 1;
  min-width: 220px;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.scope-filter-btn {
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1rem;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 0.05em;
  color: #000000;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
}

.scope-filter-btn.active {
  border: 1px solid var(--primary-color);
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.625rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.875rem;
  cursor: pointer;
}

.filter-btn:hover {
  background: #f9fafb;
}

.create-btn {
  background-color: #fff;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  color: #000;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.625rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  white-space: nowrap;
}

.create-btn:hover {
  background-color: #f9fafb;
}

.btn-icon {
  width: 13px;
  height: 13px;
  margin-left: 0;
}

.date-input >>> .date-control {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.625rem 0.9rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 200px;
}

.table-container {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: white;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.scope-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border: 1px solid transparent;
}

.scope-badge.global {
  background-color: #eef2ff;
  color: #4338ca;
  border-color: #c7d2fe;
}

.scope-badge.productvariant {
  background-color: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.actions-dropdown {
  position: relative;
}

.actions-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: white;
  font-size: 0.875rem;
  cursor: pointer;
}

.custom-dropdown-menu {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 0.25rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 999;
  min-width: 120px;
  overflow: visible;
}

.action-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
}

.action-option:hover {
  background-color: #f9fafb;
}

.text-red-500 {
  color: #ef4444;
}

.no-data-container {
  padding: 56px 20px;
  text-align: center;
  color: #333;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.no-data-icon {
  width: 42px;
  height: 42px;
  color: #cbd5e1;
  margin-bottom: 8px;
}

.no-data-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.no-data-subtext {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.loading-more-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  font-size: 0.9rem;
  color: #555;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.all-fetched-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem auto;
  color: #10b981;
  font-size: 0.95rem;
  font-weight: 500;
}

.tick-icon {
  font-size: 1.1rem;
  color: #10b981;
}
</style>
